// Who may manage a Family's purchases, wallet and reports
// (student-self-signup-prd.md §17, D-SSU-23). Mirrors the backend
// `src/families/family-manager.ts` rule so the UI shows the same doors the API
// opens. The backend stays the source of truth (CLAUDE.md §4): these helpers
// only decide what to SHOW.
//
// A family manager is either
//   - a parent (`kind=user`, role parent) of their own Family, or
//   - an independent student: a kid who signed up with their own email, whose
//     Family is the one-student `kind=student` Family, and who is not on a
//     class-code session. They manage only their OWN account.
// After a parent link (Phase 2) the Family becomes `household`, the student is
// a normal managed kid again and the parent pays and manages.

import { QueryClientContext } from '@tanstack/react-query';
import { useContext } from 'react';

import { useKidClassId } from './authStore';
import { useMe } from './useAuth';
import type { AuthPrincipal, KidPrincipal } from './types';

/** An independent student who manages their own one-student Family. */
export function isStudentManager(
  principal: AuthPrincipal | null | undefined,
  kidClassId: string | null = null,
): principal is KidPrincipal & { family_id: string } {
  return (
    principal?.kind === 'kid' &&
    principal.account_mode === 'independent' &&
    principal.family_kind === 'student' &&
    !!principal.family_id &&
    !principal.is_ephemeral &&
    !principal.class_id &&
    !kidClassId
  );
}

/**
 * The Family the signed-in principal manages: a parent's own Family or an
 * independent student's own `kind=student` Family. Null for everyone else
 * (a managed kid, a teacher, a parent with no Family yet).
 */
export function managerFamilyId(
  principal: AuthPrincipal | null | undefined,
  kidClassId: string | null = null,
): string | null {
  if (principal?.kind === 'user') return principal.family_id;
  if (isStudentManager(principal, kidClassId)) return principal.family_id;
  return null;
}

export type FamilyManager =
  | {
      kind: 'parent';
      familyId: string | null;
      /** Contact email for receipts and bookings. */
      email: string;
      displayName: string | null;
      selfKidId: null;
    }
  | {
      kind: 'student';
      familyId: string;
      /** The student's `login_email`: receipts go there. */
      email: string;
      displayName: string;
      /** The student may act only for this kid id (themselves). */
      selfKidId: string;
    };

/** Pure form of {@link useFamilyManager}, for tests and non-hook callers. */
export function familyManagerOf(
  principal: AuthPrincipal | null | undefined,
  kidClassId: string | null = null,
): FamilyManager | null {
  if (principal?.kind === 'user') {
    return {
      kind: 'parent',
      familyId: principal.family_id,
      email: principal.email,
      displayName: principal.display_name,
      selfKidId: null,
    };
  }
  if (isStudentManager(principal, kidClassId)) {
    return {
      kind: 'student',
      familyId: principal.family_id,
      email: principal.login_email ?? '',
      displayName: principal.nickname,
      selfKidId: principal.sub,
    };
  }
  return null;
}

/**
 * The family manager for the current surface's session. `manager` is null while
 * `/auth/me` loads and for a principal that manages no Family.
 */
export function useFamilyManager() {
  const me = useMe();
  const kidClassId = useKidClassId();
  return {
    manager: familyManagerOf(me.data, kidClassId),
    isLoading: me.isLoading,
    principal: me.data ?? null,
  };
}

/** The independent student's own Stars top-up page (D-SSU-23). */
export const STUDENT_SELF_TOPUP_PATH = '/learn/account/wallet/topup';

/**
 * Is the signed-in kid an independent student who pays for their own Stars?
 * Reads the kid `/auth/me` the Learn shell has already cached, without a fetch
 * and without throwing outside a QueryClientProvider, so deep studio widgets can
 * swap "ask a parent" for "top up" without threading a prop.
 */
export function useCachedStudentManager(): boolean {
  const client = useContext(QueryClientContext);
  return isStudentManager(client?.getQueryData<AuthPrincipal>(['auth', 'me', 'kid']));
}

/** Out-of-Stars hint: the student tops up themselves; every other kid asks a parent. */
export function outOfStarsHint(studentManager: boolean): string {
  return studentManager
    ? 'Out of Stars! Top up your Stars in My account.'
    : 'Out of Stars! Ask a parent to top up.';
}
